import { baseApi } from "./baseApi";
import { buildPaginationQueryString } from "./queryHelpers";
import { UpdateFeatureRequest, PaginateQuery } from "../../interfaces";
import { MediaDto } from "../../interfaces/media";
import { ControlFeatureDto } from "../../interfaces/control-feature";
import { SelectListDto } from "../../models";

export const controlFeatureApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFeatures: builder.query<
      { data: ControlFeatureDto[]; meta?: { totalItems: number } },
      PaginateQuery
    >({
      query: (data) => ({
        url: buildPaginationQueryString(data, "/api/feature"),
      }),
      providesTags: ["ControlFeatures"],
    }),
    getFeature: builder.query<ControlFeatureDto, string>({
      query: (id) => ({
        url: `/api/feature/${id}`,
      }),
      providesTags: ["ControlFeature"],
    }),
    updateFeature: builder.mutation<
      any,
      { id: string; data: Partial<UpdateFeatureRequest> }
    >({
      query: ({ id, data }) => ({
        url: `/api/feature/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "ControlFeatures",
        "ControlFeature",
        "ControlFeature-SelectList",
      ],
    }),
    deleteFeature: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/feature/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ControlFeatures", "ControlFeature-SelectList"],
    }),
    uploadFeatureIcon: builder.mutation<
      MediaDto,
      {
        file: File;
        featureId?: string;
        onProgress?: (progress: number) => void;
      }
    >({
      query: ({ file, featureId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: featureId
            ? `/api/feature/upload/icon?id=${featureId}`
            : `/api/feature/upload/icon`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        };
      },
    }),
    getFeatureSelectList: builder.query<SelectListDto[], void>({
      query: () => ({
        url: `/api/feature/select-list`,
      }),
      providesTags: ["ControlFeature-SelectList"],
    }),
    toggleFeatureState: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/feature/toggle-state/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: [
        "ControlFeature-SelectList",
        "ControlFeature",
        "ControlFeatures",
      ],
    }),
  }),
});

export const {
  useGetFeaturesQuery,
  useGetFeatureQuery,
  useUpdateFeatureMutation,
  useDeleteFeatureMutation,
  useUploadFeatureIconMutation,
  useGetFeatureSelectListQuery,
  useToggleFeatureStateMutation,
} = controlFeatureApi;
